import React, { useState, useEffect } from 'react';
import { FaStar, FaMoon, FaMosque } from 'react-icons/fa';
import { GiLantern, GiMoon, GiCrescentBlade } from 'react-icons/gi';
import styles from './EidMiladBanner.module.css';

const EidMiladBanner = () => {
    const STORAGE_KEY = 'eidMiladBannerShownSession';
    const PERMA_KEY = 'eidMiladBannerExpired';
    const EID_DATE_STR = '2025-09-04T00:00:00';
    const [isVisible, setIsVisible] = useState(() => {
        try {
            // لا تظهر أبداً إذا انتهى العداد مسبقاً (ثابت عبر الجلسات)
            const permanentlyHidden = localStorage.getItem(PERMA_KEY) === 'true';
            if (permanentlyHidden) return false;

            // تجنب الوميض: إذا التاريخ انتهى بالفعل الآن، أخفِ واحفظ العلم
            const now = new Date();
            const eidDate = new Date(EID_DATE_STR);
            if (eidDate - now <= 0) {
                try { localStorage.setItem(PERMA_KEY, 'true'); } catch (e) {}
                return false;
            }

            // قبل التاريخ: اعرض مرة واحدة لكل جلسة
            const shownThisSession = sessionStorage.getItem(STORAGE_KEY) === 'true';
            return !shownThisSession;
        } catch (e) {
            return true;
        }
    });
    const [particles, setParticles] = useState([]);
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    // إنشاء جزيئات متحركة
    useEffect(() => {
        try {
            const newParticles = Array.from({ length: 20 }, (_, i) => ({
                id: i,
                x: Math.random() * 100,
                y: Math.random() * 100,
                size: Math.random() * 4 + 2,
                speed: Math.random() * 2 + 1,
                opacity: Math.random() * 0.5 + 0.3
            }));
            setParticles(newParticles);
        } catch (error) {
            console.error('خطأ في إنشاء الجزيئات:', error);
        }
    }, []);

    // حساب الوقت المتبقي حتى المولد النبوي الشريف
    useEffect(() => {
        const calculateTimeLeft = () => {
            // تاريخ المولد النبوي الشريف: 4 سبتمبر 2025 (تاريخ مستقبلي)
            const eidMiladDate = new Date(EID_DATE_STR);
            const now = new Date();
            const difference = eidMiladDate - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                setTimeLeft({ days, hours, minutes, seconds });
            } else {
                // إذا وصلنا للتاريخ، نخفي البانر ونحفظ ذلك بشكل دائم عبر الجلسات
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                try {
                    localStorage.setItem(PERMA_KEY, 'true');
                } catch (e) {}
                setIsVisible(false);
            }
        };

        // حساب الوقت المتبقي كل ثانية
        const timer = setInterval(calculateTimeLeft, 1000);
        
        // حساب الوقت المتبقي فوراً
        calculateTimeLeft();

        return () => clearInterval(timer);
    }, []);

    // إخفاء البانر بعد 10 ثواني (مرة واحدة لكل جلسة)
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                sessionStorage.setItem(STORAGE_KEY, 'true');
            } catch (e) {}
            setIsVisible(false);
        }, 10000); // 10 ثانية

        return () => {
            clearTimeout(timer);
        };
    }, []);

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.bannerContainer}>
            {/* خلفية متدرجة */}
            <div className={styles.backgroundGradient}></div>
            
            {/* جزيئات متحركة */}
            {particles.map((particle) => (
                <div
                    key={particle.id}
                    className={styles.particle}
                    style={{
                        left: `${particle.x}%`,
                        top: `${particle.y}%`,
                        width: `${particle.size}px`,
                        height: `${particle.size}px`,
                        opacity: particle.opacity,
                        animationDuration: `${particle.speed}s`
                    }}
                />
            ))}

            {/* فوانيس طائرة من اليمين */}
            <div className={styles.flyingLanternsRight}>
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '0s' }} />
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '1s' }} />
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '2s' }} />
            </div>

            {/* فوانيس طائرة من الشمال */}
            <div className={styles.flyingLanternsLeft}>
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '0.5s' }} />
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '1.5s' }} />
                <GiLantern className={styles.flyingLantern} style={{ animationDelay: '2.5s' }} />
            </div>

            {/* المحتوى الرئيسي */}
            <div className={styles.content}>
                <div className={styles.iconContainer}>
                    <GiLantern className={styles.lampIcon} />
                    <FaMosque className={styles.mosqueIcon} />
                    <GiMoon className={styles.crescentIcon} />
                </div>
                
                <div className={styles.textContent}>
                    <h1 className={styles.title}>
                        <span className={styles.highlight}>المولد النبوي الشريف</span>
                    </h1>
                    <p className={styles.subtitle}>
                        مولد خير البشر ﷺ
                    </p>
                    <p className={styles.date}>
                        ٤ ربيع الأول ١٤٤٧ هـ
                    </p>
                    <p className={styles.gregorianDate}>
                        ٤ سبتمبر ٢٠٢٥ م - يوم الخميس
                    </p>
                    <div className={styles.infoContainer}>
                        <p className={styles.infoText}>
                            "وَمَا أَرْسَلْنَاكَ إِلَّا رَحْمَةً لِّلْعَالَمِينَ"
                        </p>
                        <p className={styles.infoSubtext}>
                            سورة الأنبياء - الآية ١٠٧
                        </p>
                    </div>
                    <div className={styles.countdownContainer}>
                        <p className={styles.countdownText}>
                            باقي على المولد النبوي الشريف
                        </p>
                        <div className={styles.countdownTimer}>
                            <span className={styles.countdownItem}>
                                <span className={styles.countdownNumber}>{timeLeft.days}</span>
                                <span className={styles.countdownLabel}>أيام</span>
                            </span>
                            <span className={styles.countdownSeparator}>:</span>
                            <span className={styles.countdownItem}>
                                <span className={styles.countdownNumber}>{timeLeft.hours.toString().padStart(2, '0')}</span>
                                <span className={styles.countdownLabel}>ساعة</span>
                            </span>
                            <span className={styles.countdownSeparator}>:</span>
                            <span className={styles.countdownItem}>
                                <span className={styles.countdownNumber}>{timeLeft.minutes.toString().padStart(2, '0')}</span>
                                <span className={styles.countdownLabel}>دقيقة</span>
                            </span>
                            <span className={styles.countdownSeparator}>:</span>
                            <span className={styles.countdownItem}>
                                <span className={styles.countdownNumber}>{timeLeft.seconds.toString().padStart(2, '0')}</span>
                                <span className={styles.countdownLabel}>ثانية</span>
                            </span>
                        </div>
                    </div>
                    <p className={styles.autoCloseMessage}>
                        ⏰ سيتم إغلاق هذا البانر تلقائياً خلال ١٠ ثوانٍ
                    </p>
                </div>

                <div className={styles.starsContainer}>
                    {[...Array(5)].map((_, i) => (
                        <FaStar 
                            key={i} 
                            className={styles.star} 
                            style={{ animationDelay: `${i * 0.2}s` }}
                        />
                    ))}
                </div>
            </div>

            {/* زر الإغلاق */}
            <button 
                className={styles.closeButton}
                onClick={() => {
                    try { sessionStorage.setItem(STORAGE_KEY, 'true'); } catch (e) {}
                    setIsVisible(false);
                }}
                aria-label="إغلاق البانر"
            >
                ×
            </button>
        </div>
    );
};

export default EidMiladBanner;
